import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, AlertCircle, ExternalLink } from 'lucide-react';
import { createHubSpotIntegration } from '@/lib/apis';
import { hubspotIntegrationAtom } from '@/store';
import { useToast } from '@/hooks/useToast';
import type { HubSpotCallbackRequest } from '@/lib/types';

export const Route = createFileRoute('/_authenticated/_layout/settings/_layout/integrations/hubspot/callback')({
  component: HubSpotCallback,
});

function HubSpotCallback() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setHubspotIntegration = useSetAtom(hubspotIntegrationAtom);
  const { toast } = useToast();

  const [isProcessing, setIsProcessing] = useState(true);
  const [hasProcessed, setHasProcessed] = useState(false);

  const createIntegrationMutation = useMutation({
    mutationFn: (data: HubSpotCallbackRequest) => createHubSpotIntegration(data),
    onSuccess: (data) => {
      if (data.success && data.integration_status) {
        setHubspotIntegration(data.integration_status);
        queryClient.invalidateQueries({ queryKey: ['hubspot', 'integration', 'status'] });
        setIsProcessing(false);
        setHasProcessed(true);
      } else {
        throw new Error(data.message || 'Integration creation failed');
      }
    },
    onError: (error) => {
      setIsProcessing(false);
      toast({
        title: 'Integration Failed',
        description: error.message || 'Failed to complete HubSpot integration',
        variant: 'destructive',
      });
    },
  });

  useEffect(() => {
    // Only process once
    if (hasProcessed) return;

    // Get URL search parameters
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');
    const error_description = urlParams.get('error_description');

    if (error) {
      setIsProcessing(false);
      toast({
        title: 'Authorization Cancelled',
        description: error_description || 'HubSpot authorization was cancelled or failed',
        variant: 'destructive',
      });
      return;
    }

    if (code && state) {
      setHasProcessed(true);
      createIntegrationMutation.mutate({ code, state });
    } else {
      setIsProcessing(false);
      toast({
        title: 'Invalid Callback',
        description: 'Missing required parameters from HubSpot callback',
        variant: 'destructive',
      });
    }
  }, [createIntegrationMutation, toast, hasProcessed]);

  const handleBackToSettings = () => {
    navigate({ to: '/settings/integrations' });
  };

  const isSuccess = !isProcessing && !createIntegrationMutation.isError && createIntegrationMutation.isSuccess;
  const isError = !isProcessing && createIntegrationMutation.isError;

  return (
    <div className="container max-w-2xl mx-auto py-8">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <img src="/hubspot.png" alt="HubSpot" className="w-6 h-6" />
            HubSpot Integration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {isProcessing && (
            <div className="text-center py-8">
              <Spinner className="w-8 h-8 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">Connecting your HubSpot account...</h3>
              <p className="text-gray-700">
                We're securely processing your authorization and setting up the integration.
              </p>
            </div>
          )}

          {isSuccess && (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2 text-green-900">
                HubSpot Successfully Connected!
              </h3>
              <p className="text-gray-700 mb-4">
                Your HubSpot account is now integrated with Glitch Ads. You can now sync contact and 
                company data to enhance your advertising campaigns.
              </p>
              <Alert className="text-left">
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>What's next?</strong> Your HubSpot data will automatically sync in the background. 
                  You can manage this integration anytime from your settings page.
                </AlertDescription>
              </Alert>
            </div>
          )}

          {isError && (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2 text-red-900">
                Connection Failed
              </h3>
              <p className="text-gray-700 mb-4">
                {createIntegrationMutation.error?.message || 
                 'We encountered an issue while connecting your HubSpot account.'}
              </p>
              <Alert variant="destructive" className="text-left mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Troubleshooting:</strong> Try connecting again from the integrations page. 
                  If the issue persists, please contact support.
                </AlertDescription>
              </Alert>
            </div>
          )}

          {!isProcessing && (
            <div className="flex justify-center gap-3">
              <Button onClick={handleBackToSettings} variant="outline">
                Back to Settings
              </Button>
              
              {isError && (
                <Button
                  onClick={() => navigate({ to: '/settings/integrations' })}
                  className="flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  Try Again
                </Button>
              )}
            </div>
          )}

          {/* Connection Details for Success State */}
          {isSuccess && createIntegrationMutation.data?.integration_status && (
            <div className="border-t pt-6">
              <h4 className="font-medium mb-3">Connection Details</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-700">Account:</span>
                  <span className="font-medium">
                    {createIntegrationMutation.data.integration_status.account_name || 'HubSpot Account'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Account ID:</span>
                  <span className="font-mono text-xs">
                    {createIntegrationMutation.data.integration_status.account_id}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Connected:</span>
                  <span>
                    {new Date().toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: '2-digit'
                    })}
                  </span>
                </div>
                {createIntegrationMutation.data.integration_status.scopes && (
                  <div className="pt-2">
                    <span className="text-gray-700 block mb-2">Permissions:</span>
                    <div className="flex flex-wrap gap-1">
                      {createIntegrationMutation.data.integration_status.scopes.map((scope) => (
                        <span
                          key={scope}
                          className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-md"
                        >
                          {scope}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}